// Keep one unresolved purchase per account. Never expire it locally: a lost
// response can hide a successful charge for arbitrarily long.
export function createAiCreditPurchaseRecovery({
    storage,
    scope,
    readStatus,
    submit,
    uuid = () => crypto.randomUUID(),
}) {
    if (!scope?.company_id || !scope?.user_id)
        throw new Error('Missing purchase scope');
    const key = `ai-credit-purchase:v1:${scope.company_id}:${scope.user_id}`;

    function load() {
        const raw = storage.getItem(key);
        if (raw === null) return null;
        const request = JSON.parse(raw);
        if (
            typeof request?.client_request_id !== 'string' ||
            !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                request.client_request_id
            ) ||
            typeof request.pack !== 'string' ||
            !request.pack ||
            request.company_id !== scope.company_id ||
            request.user_id !== scope.user_id
        )
            throw new Error('Invalid saved purchase');
        return request;
    }

    function settle(request, result) {
        if (
            !['pending', 'needs_review', 'succeeded', 'failed'].includes(
                result?.status
            ) ||
            result.client_request_id !== request.client_request_id
        ) {
            throw new Error('Unrecognized purchase response');
        }
        if (
            ['succeeded', 'failed'].includes(result.status) &&
            load()?.client_request_id === request.client_request_id
        ) {
            storage.removeItem(key);
        }
        return result;
    }

    async function recover({ retryMissing = false } = {}) {
        const request = load();
        if (!request) return null;
        const result = await readStatus(request.client_request_id);
        // A 404 is not proof the original POST failed. Only an explicit retry
        // resubmits, always using the saved ID, even if the first POST is in flight.
        if (result === null) {
            return retryMissing
                ? settle(request, await submit(request))
                : {
                      status: 'pending',
                      client_request_id: request.client_request_id,
                  };
        }
        return settle(request, result);
    }

    async function purchase(pack) {
        if (load()) return recover({ retryMissing: true });
        const request = {
            ...scope,
            pack,
            client_request_id: uuid(),
            created_at: new Date().toISOString(),
        };
        // If durable storage is unavailable, fail before making a charge request.
        storage.setItem(key, JSON.stringify(request));
        return settle(request, await submit(request));
    }

    return { load, recover, purchase };
}
